import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
} from '@angular/core';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RequestsFacade } from '../../../store/requests/requests.facade';
import { Request } from '../../../core/models/request.model';
import {
  PrintLocationDetailOption,
  PrintSiteOption,
  UserListItem,
} from 'src/app/core/models';
import { AuthService } from 'src/app/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-bulk-update-modal',
  standalone: true,
  imports: [ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './bulk-update-modal.component.html',
  styleUrls: ['./bulk-update-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUpdateModalComponent implements OnInit {
  @Input() selectedRequests: Request[] = [];
  @Output() closeModal = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  private readonly facade = inject(RequestsFacade);
  public readonly authService = inject(AuthService);

  form!: FormGroup;
  isSubmitting = false;

  // Filter options
  availablePrintSites: PrintSiteOption[] = [];
  availablePrintLocations: PrintLocationDetailOption[] = [];
  availableUsers: UserListItem[] = [];
  currentUserEmail: string | null = null;
  currentUserId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  get isPrintLocationDisabled(): boolean {
    const printSiteValue = this.form?.get('printSiteId')?.value;
    return !printSiteValue || printSiteValue === '';
  }

  ngOnInit(): void {
    // Subscribe to current user to get email
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserEmail = user?.email ?? null;
        this.currentUserId = user?.id ?? null;
      });
    this.initializeForm();
    this.facade.loadUserList();
    this.facade.loadPrintSites();

    this.loadDropdownData();
    this.watchPrintSiteChanges();
  }

  private watchPrintSiteChanges(): void {
    this.form.get('printSiteId')?.valueChanges.subscribe(printSiteId => {
      this.form.get('printLocationId')?.reset('');
      if (printSiteId) {
        this.facade.loadPrintLocationsBySite(Number(printSiteId));
      }
    });
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      assignedUser: [''],
      printSiteId: ['', Validators.required],
      printLocationId: ['', Validators.required],
      printPostage: [false],
      comment: [''],
    });
  }

  private loadDropdownData(): void {
    this.facade.userList$.subscribe(users => {
      this.availableUsers = users ?? [];
    });

    this.facade.printSites$.subscribe(printSites => {
      this.availablePrintSites = printSites ?? [];
    });

    this.facade.printLocationsBySite$.subscribe(printLocations => {
      this.availablePrintLocations = printLocations ?? [];
    });
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSubmit(): void {
    if (this.form.valid && this.selectedRequests.length > 0) {
      this.isSubmitting = true;
      const formValue = this.form.value;

      // Build request updates for each selected request
      const payload = {
        requestUpdates: this.selectedRequests.map(request => ({
          requestId: Number(request.id),
        })),

        printSiteId: Number(formValue.printSiteId),

        assignedUser: Number(formValue.assignedUser),

        printLocationId: Number(formValue.printLocationId),

        printerType: 'HP',

        sortOrder: 'Backwards',

        printPostage: formValue.printPostage,

        comment: formValue.comment ?? '',

        modifiedBy: this.currentUserEmail ?? 'currentUser', // replace later with logged-in user
      };

      this.facade.bulkUpdateRequests(payload);

      this.closeModal.emit();
    }
  }

  get selectedRequestsCount(): number {
    return this.selectedRequests.length;
  }
}
